import React, { useState, useEffect } from 'react';
import { getPendingVisits, moderateVisit } from '../../utils/adminApi';
import './ModerateVisitsTab.css';

const ModerateVisitsTab = () => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingVisit, setProcessingVisit] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    loadVisits();
  }, []);

  const loadVisits = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getPendingVisits();
      setVisits(data.visits);
    } catch (err) {
      setError('Failed to load pending visits');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (visitId) => {
    setProcessingVisit(visitId);
    setError(null);
    
    try {
      await moderateVisit({ visitId, approved: true });
      alert('Visit approved!');
      loadVisits();
    } catch (err) {
      setError('Failed to approve visit');
      console.error(err);
    } finally {
      setProcessingVisit(null);
    }
  };

  const handleReject = async (visitId) => {
    if (!window.confirm('Are you sure you want to reject this visit? The user will be notified.')) {
      return;
    }
    
    setProcessingVisit(visitId);
    setError(null);
    
    try {
      await moderateVisit({ visitId, approved: false });
      alert('Visit rejected');
      loadVisits();
    } catch (err) {
      setError('Failed to reject visit');
      console.error(err);
    } finally {
      setProcessingVisit(null);
    }
  };

  if (loading) {
    return <div className="tab-loading">Loading pending visits...</div>;
  }

  return (
    <div className="moderate-visits-tab">
      <h2>Visits Pending Moderation</h2>
      
      {error && (
        <div className="error-banner">{error}</div>
      )}
      
      {visits.length === 0 && (
        <div className="no-visits">
          <p>No visits pending moderation</p>
        </div>
      )}
      
      {visits.map(visit => (
        <div key={visit.visitId} className="visit-card">
          <div className="visit-header">
            <div>
              <h3>{visit.userName}</h3>
              <span className="visit-email">{visit.userEmail}</span>
            </div>
            <div className="visit-meta">
              <span className="visit-type-badge">{visit.visitType}</span>
              <span className="privacy-badge">{visit.privacyLevel}</span>
            </div>
          </div>
          
          <div className="visit-details">
            <p><strong>Stone:</strong> {visit.stoneId}</p>
            <p><strong>Submitted:</strong> {new Date(visit.submittedDate).toLocaleString()}</p>
          </div>
          
          {visit.memoryText && (
            <div className="visit-memory">
              <strong>Memory/Thought:</strong>
              <p>{visit.memoryText}</p>
            </div>
          )}
          
          {visit.mediaFiles && visit.mediaFiles.length > 0 && (
            <div className="visit-media">
              <strong>Media Files ({visit.mediaFiles.length}):</strong>
              <div className="media-grid">
                {visit.mediaFiles.map((media, index) => {
                  // Derive type from fileType if type is missing (backward compatibility)
                  const mediaType = media.type || (media.fileType?.startsWith('image/') ? 'image' : 'video');
                  
                  return (
                    <div
                      key={index}
                      className="media-thumb"
                      onClick={() => setSelectedMedia({ ...media, type: mediaType })}
                    >
                      {mediaType === 'image' && (
                        <img src={media.url} alt="Visit media" />
                      )}
                      {mediaType === 'video' && (
                        <div className="video-thumb">
                          <video src={media.url} />
                          <div className="play-icon">▶</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          <div className="visit-actions">
            <button
              onClick={() => handleApprove(visit.visitId)}
              disabled={processingVisit === visit.visitId}
              className="approve-button"
            >
              Approve
            </button>
            <button
              onClick={() => handleReject(visit.visitId)}
              disabled={processingVisit === visit.visitId}
              className="reject-button"
            >
              Reject
            </button>
          </div>
        </div>
      ))}
      
      {selectedMedia && (
        <div className="media-viewer" onClick={() => setSelectedMedia(null)}>
          <button className="close-viewer">×</button>
          <div className="viewer-content" onClick={(e) => e.stopPropagation()}>
            {selectedMedia.type === 'image' && (
              <img src={selectedMedia.url} alt="Full size" />
            )}
            {selectedMedia.type === 'video' && (
              <video src={selectedMedia.url} controls autoPlay />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModerateVisitsTab;